import {
  Flame,
  Droplet,
  Hexagon,
  Mountain,
  Circle,
  ArrowUp,
  Mars,
  Venus,
  Medal,
  Trophy,
} from "lucide-react";
import { twMerge } from "tailwind-merge";
import { pos_txt } from "./cn_map";

/**
 * Get icon component and styling for an element type
 * @param {string} element - The element type (fire, water, metal, earth)
 * @returns {Object|null} Object with icon component and color class, or null if element not found
 */
const elementMap = {
  fire: {
    icontxt: "Flame",
    icon: Flame,
    color: "text-red-500",
  },
  water: {
    icontxt: "Droplet",
    icon: Droplet,
    color: "text-blue-500",
  },
  metal: {
    icontxt: "Hexagon",
    icon: Hexagon,
    color: "text-gray-400",
  },
  earth: {
    icontxt: "Mountain",
    icon: Mountain,
    color: "text-amber-600",
  },
};
export const getElementIcon = (element) => {
  return elementMap[element?.toLowerCase()] || null;
};
export const getElementIconTxt = (element) => {
  return elementMap[element?.toLowerCase()]?.icontxt || "";
};

export const getElementColor = (element) => {
  return elementMap[element?.toLowerCase()]?.color || "text-white";
};

/**
 * Get icon component and styling for gender/planetary symbols
 * @param {string} gender - The gender type (male, female, mars, venus)
 * @returns {Object|null} Object with icon component and color class, or null if gender not found
 */

const genderMap = {
  male: { icon: Mars, color: "text-blue-600" },
  mars: { icon: Mars, color: "text-blue-600" },
  female: { icon: Venus, color: "text-pink-500" },
  venus: { icon: Venus, color: "text-pink-500" },
};
export const getGenderIcon = (gender) => {
  return genderMap[gender?.toLowerCase()] || null;
};

/**
 * Render an element icon with proper styling
 * @param {string} element - The element type
 * @param {string} size - Icon size class (default: "w-6 h-6")
 * @param {string} className - Additional CSS classes
 * @returns {JSX.Element|null} The rendered icon or null
 */
export const ElementIcon = ({ element, size = "w-6 h-6", className = "" }) => {
  const elementData = getElementIcon(element);
  if (!elementData) return null;

  const { icon: IconComponent, color } = elementData;
  return <IconComponent className={twMerge(size, color, className)} />;
};

/**
 * Render a gender/planetary icon with proper styling
 * @param {string} gender - The gender type
 * @param {string} size - Icon size class (default: "w-6 h-6")
 * @param {string} className - Additional CSS classes
 * @returns {JSX.Element|null} The rendered icon or null
 */
export const GenderIcon = ({ gender, size = "w-6 h-6", className = "" }) => {
  const genderData = getGenderIcon(gender);
  if (!genderData) return null;

  const { icon: IconComponent, color } = genderData;

  return <IconComponent className={twMerge(size, color, className)} />;
};

/**
 * Render either an element or gender icon based on type detection
 * @param {string} type - The type (element or gender)
 * @param {string} size - Icon size class (default: "w-6 h-6")
 * @param {string} className - Additional CSS classes
 * @returns {JSX.Element|null} The rendered icon or null
 */
export const UniversalIcon = ({ type, size = "w-6 h-6", className = "" }) => {
  // Try element first, then gender
  const elementData = getElementIcon(type);
  if (elementData) {
    return <ElementIcon element={type} size={size} className={className} />;
  }

  const genderData = getGenderIcon(type);
  if (genderData) {
    return <GenderIcon gender={type} size={size} className={className} />;
  }

  return null;
};

// Export the mapping objects for external use if needed
export const ELEMENT_TYPES = {
  FIRE: "fire",
  WATER: "water",
  METAL: "metal",
  EARTH: "earth",
};

export const GENDER_TYPES = {
  MALE: "male",
  MARS: "mars",
  FEMALE: "female",
  VENUS: "venus",
};

export const getGenderColor = (gender) => {
  return genderMap[gender?.toLowerCase()]?.color || "text-white";
};

const positionMap = {
  1: {
    bg: "bg-yellow-400/20",
    text: "text-yellow-400",
    icon: Trophy,
  },
  2: {
    bg: "bg-gray-300/20",
    text: "text-gray-200",
    icon: Medal,
  },
  3: {
    bg: "bg-amber-600/20",
    text: "text-amber-600",
    icon: Medal,
  },
  def: {
    bg: "bg-gray-600/10",
    text: "text-gray-400",
    icon: Medal,
  },
};

export const PositionIcon = ({ pos, size = "w-4 h-4", className = "" }) => {
  const positionData = positionMap[pos] || positionMap.def;
  const { bg, text, icon: IconComponent } = positionData;

  return (
    <div
      class={twMerge(
        bg,
        text,
        "fr-sc resp-gap-2 resp-px-2 rounded-md",
        className,
      )}
    >
      <IconComponent className={size} />
      <span className="font-bold">{pos_txt(pos)}</span>
    </div>
  );
};

const typeColorMap = {
  genesis: "text-cyan-400",
  morphed: "text-purple-400",
  freak: "text-orange-400",
  xclass: "text-pink-400",
};
export const getTypeColor = (type) => typeColorMap[type] || "text-gray-400";
